import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CardWithHeader } from "@/components/card-with-header";
import { TimePicker } from "@/components/time-picker";
import { useMerchant } from "@/hooks/use-merchant";
import { useAnalytics } from "@/lib/analytics";
import { hasRole } from "@/lib/auth";
import { sendFxRateAlertTest } from "@/lib/fx-rate-alerts-api";
import {
  fxNotificationConfigQuery,
  fxRateAlertConfigQuery,
  replaceFxNotificationConfig,
  replaceFxRateAlertConfig,
  type FxNotificationConfig,
  type FxRateAlertConfig,
} from "@/lib/fx-notification-settings-api";
import { VolumeApi, type GetMerchantAccountsResponse } from "@/lib/volume-api";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, Navigate, redirect } from "@tanstack/react-router";
import {
  ArrowRightLeft,
  ArrowUpCircle,
  BellRing,
  MoveRight,
  Send,
  TriangleAlert,
  UserRound,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { useSelectedApplication } from "@/hooks/use-selected-application";
import {
  FX_WHATSAPP_NOTIFICATIONS_FLAG,
  useIsFeatureFlagEnabled,
} from "@/lib/feature-flags";
import {
  NotificationRow,
  RecipientTable,
  type NotificationChannel,
  type RecipientDestination,
} from "./-components/notification-settings";
import {
  RATE_DESTINATIONS,
  RATE_SOURCES,
  buildRateAlertPairs,
  configFingerprint,
  emptyFormValues,
  isWhatsAppGroupRecipient,
  rateAlertSummary,
  timezoneSelectOptions,
  toggleValue,
  valuesFromSavedConfig,
  type RateAlertFormValues,
} from "./-components/rate-alert-form";
import { CurrencyPillGroup } from "./-components/currency-pill-group";
import { WhatsAppComingSoonDialog } from "./-components/whatsapp-coming-soon-dialog";

type NotificationType = "trade_completed" | "payout_completed";
type Draft = Record<NotificationType, RecipientDestination[]>;
type NotificationSettingsFormValues = RateAlertFormValues & {
  readonly notificationDestinations: Draft;
};

const NOTIFICATIONS: readonly {
  readonly type: NotificationType;
  readonly title: string;
  readonly description: string;
  readonly icon: typeof ArrowRightLeft;
}[] = [
  {
    type: "trade_completed",
    title: "Trade completed",
    description: "Sent when an FX trade reaches completed status.",
    icon: ArrowRightLeft,
  },
  {
    type: "payout_completed",
    title: "Payout completed",
    description: "Sent when an FX payout reaches completed status.",
    icon: ArrowUpCircle,
  },
];

const RATE_ALERTS_ROW_KEY = "rate_alerts";
const RATE_ALERTS_TITLE = "Rate alerts";

const emptyDraft = (): Draft => ({
  trade_completed: [],
  payout_completed: [],
});

const destinationKey = (destination: RecipientDestination) =>
  `${destination.userId}:${destination.channel}`;

const normalize = (values: readonly RecipientDestination[]) =>
  [
    ...new Map(
      values.map((destination) => [destinationKey(destination), destination]),
    ).values(),
  ].sort((left, right) =>
    destinationKey(left).localeCompare(destinationKey(right)),
  );

const sameDestinations = (
  a: readonly RecipientDestination[],
  b: readonly RecipientDestination[],
) => {
  const left = normalize(a);
  const right = normalize(b);
  return (
    left.length === right.length &&
    left.every((value, index) => {
      const rightValue = right[index];
      return (
        rightValue !== undefined &&
        destinationKey(value) === destinationKey(rightValue)
      );
    })
  );
};

export const Route = createFileRoute(
  "/(app)/_main-layout/_exchange/exchange/notifications",
)({
  component: NotificationsPage,
  beforeLoad: ({ context }) => {
    if (
      !hasRole("EDIT_SETTINGS", context.authorities) &&
      !hasRole("ACCOUNT_ADMIN", context.authorities)
    ) {
      throw redirect({ to: "/exchange", search: true });
    }
  },
  loader: async ({ context }) => {
    const api = new VolumeApi(
      context.accessToken,
      context.selectedApplicationId,
    );
    const requestContext = {
      accessToken: context.accessToken,
      applicationId: context.selectedApplicationId,
    };
    const [users] = await Promise.all([
      api.getMerchantAccounts({ merchantId: context.merchantId }),
      context.queryClient.ensureQueryData(
        fxNotificationConfigQuery(requestContext),
      ),
      context.queryClient.ensureQueryData(
        fxRateAlertConfigQuery(requestContext),
      ),
    ]);
    const activeUsers = users.filter((user) => user.active);
    const loggedInUser =
      activeUsers.find(
        (user) => user.email.toLowerCase() === context.email?.toLowerCase(),
      ) ??
      activeUsers.find(
        (user) => user.userId === context.decodedJwt.userAccountId,
      );
    return {
      accessToken: context.accessToken,
      users: activeUsers,
      userEmail: context.email,
      userName: loggedInUser?.name || context.email || "Unknown user",
    };
  },
});

function savedDraftFromConfig(
  config: readonly FxNotificationConfig[],
  activeUsers: GetMerchantAccountsResponse,
): Draft {
  const activeUserIds = new Set(activeUsers.map((user) => user.userId));
  const draft = emptyDraft();
  for (const row of config) {
    draft[row.type] = normalize(
      row.recipients
        .filter((recipient) => activeUserIds.has(recipient.userId))
        .map((recipient) => ({
          userId: recipient.userId,
          channel: recipient.channel,
        })),
    );
  }
  return draft;
}

const formValuesFromSavedConfig = (
  config: readonly FxNotificationConfig[],
  savedRateAlert: FxRateAlertConfig | null,
  users: GetMerchantAccountsResponse,
): NotificationSettingsFormValues => ({
  ...valuesFromSavedConfig(savedRateAlert),
  notificationDestinations: savedDraftFromConfig(config, users),
});

function NotificationsPage() {
  const application = useSelectedApplication();
  const canSeeExchangeNotifications = useIsFeatureFlagEnabled(
    "fx-exchange-notifications",
  );

  if (!canSeeExchangeNotifications) return <Navigate to="/exchange" />;
  if (!application) return <div>Error: Application not found</div>;
  if (!application.hasVirtualAccountsEnabled)
    return <Navigate to="/exchange" />;

  return <NotificationsSettings applicationId={application.applicationId} />;
}

function NoUsersNote() {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-muted-foreground">
      <UserRound />
      No active users are available to receive notifications.
    </div>
  );
}

function NotificationsSettings({
  applicationId,
}: {
  readonly applicationId: string;
}) {
  const { accessToken, users, userEmail, userName } = Route.useLoaderData();
  const merchant = useMerchant();
  const requestContext = { accessToken, applicationId };
  const queryClient = useQueryClient();
  const config = useSuspenseQuery(fxNotificationConfigQuery(requestContext))
    .data.notifications;
  const savedRateAlert = useSuspenseQuery(
    fxRateAlertConfigQuery(requestContext),
  ).data.config;
  const analytics = useAnalytics();
  const whatsAppEnabled = useIsFeatureFlagEnabled(
    FX_WHATSAPP_NOTIFICATIONS_FLAG,
  );
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = useState(false);
  const whatsAppInterestSource = useRef<{
    readonly context: string;
    readonly selectionScope: "all" | "recipient";
  } | null>(null);
  const [openRows, setOpenRows] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const setRowOpen = (key: string, open: boolean) =>
    setOpenRows((current) => {
      const next = new Set(current);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });

  const showWhatsAppComingSoon = (input: {
    readonly context: string;
    readonly selectionScope: "all" | "recipient";
  }) => {
    analytics.track("fx_whatsapp_notification_checkbox_clicked", {
      application_id: applicationId,
      merchant_name: merchant.companyName,
      request: `Explored WhatsApp notifications for ${input.context}`,
      context: input.context,
      selection_scope: input.selectionScope,
      user_email: userEmail,
      user_name: userName,
    });
    whatsAppInterestSource.current = input;
    setWhatsAppDialogOpen(true);
  };

  const savedDraft = savedDraftFromConfig(config, users);
  const form = useForm<NotificationSettingsFormValues>({
    defaultValues: {
      ...emptyFormValues(),
      notificationDestinations: emptyDraft(),
    },
  });
  const draft = useWatch({
    control: form.control,
    name: "notificationDestinations",
  });
  const sources = useWatch({ control: form.control, name: "sources" });
  const destinations = useWatch({
    control: form.control,
    name: "destinations",
  });
  const emailUserIds = useWatch({
    control: form.control,
    name: "emailUserIds",
  });
  const whatsAppUserIds = useWatch({
    control: form.control,
    name: "whatsAppUserIds",
  });
  const watchedGroups = useWatch({ control: form.control, name: "groups" });
  const cadence = useWatch({ control: form.control, name: "cadence" });
  const dailyTime = useWatch({ control: form.control, name: "dailyTime" });
  const timezone = useWatch({ control: form.control, name: "timezone" });
  const groups = watchedGroups.filter(isWhatsAppGroupRecipient);

  useEffect(() => {
    form.reset(formValuesFromSavedConfig(config, savedRateAlert, users));
  }, [config, form, savedRateAlert, users]);

  const dirtyNotifications = NOTIFICATIONS.filter(
    (notification) =>
      !sameDestinations(
        draft[notification.type],
        savedDraft[notification.type],
      ),
  );

  const toggle = (
    type: NotificationType,
    userId: string,
    channel: NotificationChannel,
  ) => {
    if (channel === "whatsapp" && !whatsAppEnabled) return;
    const current = form.getValues(`notificationDestinations.${type}`);
    const destination = { userId, channel };
    const key = destinationKey(destination);
    const isSelected = current.some(
      (currentDestination) => destinationKey(currentDestination) === key,
    );
    form.setValue(
      `notificationDestinations.${type}`,
      normalize(
        isSelected
          ? current.filter(
              (currentDestination) =>
                destinationKey(currentDestination) !== key,
            )
          : [...current, destination],
      ),
      { shouldDirty: true },
    );
  };

  const toggleAll = (type: NotificationType, channel: NotificationChannel) => {
    if (channel === "whatsapp" && !whatsAppEnabled) return;
    const eligibleUsers = users.filter(
      (user) => channel === "email" || Boolean(user.phoneNumber),
    );
    const current = form.getValues(`notificationDestinations.${type}`);
    const selectedUserIds = new Set<string>();
    for (const destination of current) {
      if (destination.channel === channel) {
        selectedUserIds.add(destination.userId);
      }
    }
    const allSelected = eligibleUsers.every((user) =>
      selectedUserIds.has(user.userId),
    );
    const otherChannels = current.filter(
      (destination) => destination.channel !== channel,
    );
    form.setValue(
      `notificationDestinations.${type}`,
      allSelected
        ? otherChannels
        : normalize([
            ...otherChannels,
            ...eligibleUsers.map((user) => ({
              userId: user.userId,
              channel,
            })),
          ]),
      { shouldDirty: true },
    );
  };

  const savedFingerprint = configFingerprint(
    valuesFromSavedConfig(savedRateAlert),
  );
  const rateAlertDirty =
    configFingerprint({
      ...emptyFormValues(),
      sources,
      destinations,
      cadence,
      dailyTime,
      timezone,
      emailUserIds,
      whatsAppUserIds,
      groups,
    }) !== savedFingerprint;

  const pairs = buildRateAlertPairs(sources, destinations);
  const rateAlertSaveBlocked = rateAlertDirty && pairs.length === 0;
  const emailUserIdSet = new Set(emailUserIds);
  const whatsAppUserIdSet = new Set(whatsAppUserIds);
  const whatsAppTestRecipients = users.flatMap((user) =>
    whatsAppUserIdSet.has(user.userId) && user.phoneNumber
      ? [
          {
            recipientType: "user" as const,
            target: user.phoneNumber,
          },
        ]
      : [],
  );
  const timezoneOptions = timezoneSelectOptions(timezone);

  const rateAlertRecipients: RecipientDestination[] = [
    ...emailUserIds.map((userId) => ({
      userId,
      channel: "email" as const,
    })),
    ...whatsAppUserIds.map((userId) => ({
      userId,
      channel: "whatsapp" as const,
    })),
  ];

  const toggleRateAlertRecipient = (
    userId: string,
    channel: NotificationChannel,
  ) => {
    if (channel === "whatsapp" && !whatsAppEnabled) return;
    const field = channel === "email" ? "emailUserIds" : "whatsAppUserIds";
    const current = channel === "email" ? emailUserIds : whatsAppUserIds;
    form.setValue(field, toggleValue(current, userId), { shouldDirty: true });
  };

  const toggleAllRateAlertRecipients = (channel: NotificationChannel) => {
    if (channel === "whatsapp" && !whatsAppEnabled) return;
    const eligibleUsers = users.filter(
      (user) => channel === "email" || Boolean(user.phoneNumber),
    );
    const field = channel === "email" ? "emailUserIds" : "whatsAppUserIds";
    const current = channel === "email" ? emailUserIds : whatsAppUserIds;
    const selectedUserIds = new Set(current);
    const allSelected = eligibleUsers.every((user) =>
      selectedUserIds.has(user.userId),
    );
    form.setValue(
      field,
      allSelected ? [] : eligibleUsers.map((user) => user.userId),
      { shouldDirty: true },
    );
  };

  const saveNotifications = async () => {
    const saved = await replaceFxNotificationConfig(requestContext, {
      notifications: NOTIFICATIONS.map((notification) => ({
        type: notification.type,
        destinations: draft[notification.type],
      })),
    });
    queryClient.setQueryData(
      fxNotificationConfigQuery(requestContext).queryKey,
      saved,
    );
  };

  const saveRateAlert = async () => {
    const saved = await replaceFxRateAlertConfig(requestContext, {
      cadence,
      dailyTime: cadence === "daily" ? dailyTime : null,
      timezone,
      pairs,
      recipients: [
        ...users.flatMap((user) =>
          emailUserIdSet.has(user.userId)
            ? [
                {
                  recipientType: "user" as const,
                  userId: user.userId,
                  channel: "email" as const,
                  displayName: user.name || user.email,
                },
              ]
            : [],
        ),
        ...users.flatMap((user) =>
          whatsAppUserIdSet.has(user.userId) && user.phoneNumber
            ? [
                {
                  recipientType: "user" as const,
                  userId: user.userId,
                  channel: "whatsapp" as const,
                  displayName: user.name || user.email,
                },
              ]
            : [],
        ),
        ...groups.map((group) => ({
          recipientType: "group" as const,
          groupId: group.groupId,
          displayName: group.displayName,
        })),
      ],
    });
    queryClient.setQueryData(
      fxRateAlertConfigQuery(requestContext).queryKey,
      saved,
    );
  };

  const saveMutation = useMutation({
    mutationFn: (sections: {
      readonly notifications: boolean;
      readonly rateAlert: boolean;
    }) =>
      Promise.all([
        sections.notifications ? saveNotifications() : undefined,
        sections.rateAlert ? saveRateAlert() : undefined,
      ]),
  });

  const dirtySections = [
    ...dirtyNotifications.map((notification) => notification.title),
    ...(rateAlertDirty ? [RATE_ALERTS_TITLE] : []),
  ];

  const save = async () => {
    if (rateAlertSaveBlocked) {
      toast.error(
        "Select at least one From and To currency before saving rate alerts.",
      );
      return;
    }
    await toast
      .promise(
        saveMutation.mutateAsync({
          notifications: dirtyNotifications.length > 0,
          rateAlert: rateAlertDirty,
        }),
        {
          loading: "Saving FX notification settings",
          success: "FX notification settings saved.",
          error: "There was a problem saving FX notification settings.",
        },
      )
      .unwrap();
  };

  const discard = () => {
    form.reset(formValuesFromSavedConfig(config, savedRateAlert, users));
  };

  const rateAlertRecipientCount =
    emailUserIds.length + (whatsAppEnabled ? whatsAppUserIds.length : 0);
  const sendTestDisabled =
    !whatsAppEnabled ||
    pairs.length === 0 ||
    whatsAppTestRecipients.length === 0;

  return (
    <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(save)}>
      <CardWithHeader
        contained
        title="FX notifications"
        noChildPadding
        headerContent={
          <div className="flex items-center gap-2">
            {dirtySections.length > 0 && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700"
                title={`Unsaved changes: ${dirtySections.join(" · ")}`}
              >
                <TriangleAlert aria-hidden className="size-3.5" />
                Unsaved changes
              </span>
            )}
            {dirtySections.length > 0 && (
              <Button type="button" variant="ghost" onClick={discard}>
                Discard
              </Button>
            )}
            <Button
              type="submit"
              variant="gradient"
              disabled={
                dirtySections.length === 0 ||
                saveMutation.isPending ||
                rateAlertSaveBlocked
              }
              title={
                rateAlertSaveBlocked
                  ? "Select at least one From and To currency before saving rate alerts"
                  : undefined
              }
            >
              Save changes
            </Button>
          </div>
        }
      >
        <p className="max-w-3xl px-4 pb-2 pt-4 text-sm text-muted-foreground sm:px-5 sm:pt-5">
          Every FX notification in one list. Open a row to choose who receives
          it by email
          {whatsAppEnabled ? ", WhatsApp, or both" : ""}. A notification is live
          as soon as it has a recipient.
        </p>

        <div>
          {NOTIFICATIONS.map((notification) => {
            const rowDestinations = draft[notification.type];
            return (
              <NotificationRow
                key={notification.type}
                icon={notification.icon}
                title={notification.title}
                subtitle={notification.description}
                emailCount={
                  rowDestinations.filter(
                    (destination) => destination.channel === "email",
                  ).length
                }
                whatsAppCount={
                  rowDestinations.filter(
                    (destination) => destination.channel === "whatsapp",
                  ).length
                }
                active={rowDestinations.some(
                  (destination) =>
                    destination.channel === "email" || whatsAppEnabled,
                )}
                open={openRows.has(notification.type)}
                onOpenChange={(open) => setRowOpen(notification.type, open)}
              >
                {users.length === 0 ? (
                  <NoUsersNote />
                ) : (
                  <RecipientTable
                    users={users}
                    selected={rowDestinations}
                    onToggle={(userId, channel) =>
                      toggle(notification.type, userId, channel)
                    }
                    onToggleAll={(channel) =>
                      toggleAll(notification.type, channel)
                    }
                    context={`${notification.title.toLowerCase()} notifications`}
                    whatsAppEnabled={whatsAppEnabled}
                    onWhatsAppUnavailableClick={showWhatsAppComingSoon}
                  />
                )}
              </NotificationRow>
            );
          })}

          <NotificationRow
            icon={BellRing}
            title="Indicative rate alerts"
            subtitle={
              rateAlertSummary({
                pairCount: pairs.length,
                cadence,
                dailyTime,
                timezone,
              }) ??
              (whatsAppEnabled
                ? "Scheduled currency-pair rates delivered by email, WhatsApp, or both."
                : "Scheduled currency-pair rates delivered by email.")
            }
            emailCount={emailUserIds.length}
            whatsAppCount={whatsAppUserIds.length}
            active={pairs.length > 0 && rateAlertRecipientCount > 0}
            open={openRows.has(RATE_ALERTS_ROW_KEY)}
            onOpenChange={(open) => setRowOpen(RATE_ALERTS_ROW_KEY, open)}
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4 lg:flex-row">
                  <CurrencyPillGroup
                    label="From"
                    codes={RATE_SOURCES}
                    selected={sources}
                    onToggle={(code) =>
                      form.setValue("sources", toggleValue(sources, code), {
                        shouldDirty: true,
                      })
                    }
                  />
                  <MoveRight
                    aria-hidden
                    className="mt-8 hidden h-5 w-5 shrink-0 text-slate-300 lg:block"
                  />
                  <CurrencyPillGroup
                    label="To"
                    codes={RATE_DESTINATIONS}
                    selected={destinations}
                    onToggle={(code) =>
                      form.setValue(
                        "destinations",
                        toggleValue(destinations, code),
                        { shouldDirty: true },
                      )
                    }
                    className="flex-1"
                  />
                </div>
                {pairs.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="mr-1 text-xs font-medium text-slate-500">
                      {pairs.length} rate{pairs.length === 1 ? "" : "s"} per
                      message:
                    </span>
                    {pairs.map((pair) => (
                      <span
                        key={`${pair.sourceCurrency}-${pair.destinationCurrency}`}
                        className="inline-flex items-center gap-1 rounded-full bg-volume-text-purple/10 px-2 py-0.5 text-xs font-semibold text-volume-text-purple"
                      >
                        {pair.sourceCurrency}
                        <MoveRight aria-hidden className="h-3 w-3" />
                        {pair.destinationCurrency}
                      </span>
                    ))}
                  </div>
                )}
                {rateAlertSaveBlocked && (
                  <p className="text-xs font-medium text-destructive">
                    Select at least one From and To currency before saving rate
                    alerts.
                  </p>
                )}
                <div className="flex flex-wrap items-end gap-3">
                  {/* relative anchors Radix Select's hidden form <select> so it
                      can't extend the document scroll area */}
                  <label className="relative block space-y-1">
                    <span className="block text-xs font-medium text-slate-500">
                      Frequency
                    </span>
                    <Select
                      value={cadence}
                      onValueChange={(option) =>
                        form.setValue("cadence", option as "hourly" | "daily", {
                          shouldDirty: true,
                        })
                      }
                    >
                      <SelectTrigger className="w-36 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Every hour</SelectItem>
                        <SelectItem value="daily">Once a day</SelectItem>
                      </SelectContent>
                    </Select>
                  </label>
                  {cadence === "daily" && (
                    <div className="space-y-1">
                      <span className="block text-xs font-medium text-slate-500">
                        Time
                      </span>
                      <TimePicker
                        value={dailyTime}
                        ariaLabel="Send time"
                        className="w-28"
                        onChange={(time) =>
                          form.setValue("dailyTime", time, {
                            shouldDirty: true,
                          })
                        }
                      />
                    </div>
                  )}
                  <label className="w-full max-w-72 space-y-1">
                    <span className="block text-xs font-medium text-slate-500">
                      Timezone
                    </span>
                    <SearchableSelect
                      options={timezoneOptions}
                      value={timezone}
                      onChange={(zone) =>
                        form.setValue("timezone", zone, { shouldDirty: true })
                      }
                      placeholder="Select timezone"
                      searchPlaceholder="Search timezones..."
                    />
                  </label>
                </div>
              </div>

              {users.length === 0 ? (
                <NoUsersNote />
              ) : (
                <RecipientTable
                  users={users}
                  selected={rateAlertRecipients}
                  onToggle={toggleRateAlertRecipient}
                  onToggleAll={toggleAllRateAlertRecipients}
                  context="rate alerts"
                  whatsAppEnabled={whatsAppEnabled}
                  onWhatsAppUnavailableClick={showWhatsAppComingSoon}
                />
              )}

              {whatsAppEnabled && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200/80 pt-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-volume-text-purple/40 text-volume-text-purple hover:bg-volume-light-purple hover:text-volume-text-purple"
                      disabled={sendTestDisabled}
                      title={
                        sendTestDisabled
                          ? whatsAppEnabled
                            ? "Select at least one currency pair and one WhatsApp destination first"
                            : "WhatsApp test sends are coming soon"
                          : undefined
                      }
                      onClick={() =>
                        void toast.promise(
                          sendFxRateAlertTest({
                            accessToken,
                            applicationId,
                            pairs,
                            recipients: whatsAppTestRecipients,
                          }),
                          {
                            loading: "Sending test FX rate alert",
                            success: ({ sent }) =>
                              `Test alert sent to ${sent} recipient${sent === 1 ? "" : "s"}.`,
                            error:
                              "There was a problem sending the test FX rate alert.",
                          },
                        )
                      }
                    >
                      <Send className="mr-1.5 size-3.5" />
                      Send test now
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {whatsAppEnabled
                        ? "Sends the selected rates to your WhatsApp recipients right away."
                        : "WhatsApp test sends are temporarily unavailable."}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">
                    Rates are indicative, not binding quotes.
                  </span>
                </div>
              )}
            </div>
          </NotificationRow>
        </div>

        {whatsAppEnabled && (
          <p className="px-4 pb-4 pt-3 text-xs text-muted-foreground sm:px-5">
            WhatsApp group delivery is coming soon. Until then, rate alerts go
            to the individual recipients selected above.
            {groups.length > 0 &&
              ` Already added: ${groups
                .map((group) => group.displayName)
                .join(
                  ", ",
                )} — these will start receiving alerts once group delivery is live.`}
          </p>
        )}
      </CardWithHeader>
      <WhatsAppComingSoonDialog
        open={whatsAppDialogOpen}
        onOpenChange={setWhatsAppDialogOpen}
        onRequestUpdates={() => {
          analytics.track("fx_whatsapp_notification_updates_requested", {
            application_id: applicationId,
            merchant_name: merchant.companyName,
            request: `Asked for WhatsApp availability updates for ${
              whatsAppInterestSource.current?.context ?? "FX notifications"
            }`,
            context: whatsAppInterestSource.current?.context,
            selection_scope: whatsAppInterestSource.current?.selectionScope,
            user_email: userEmail,
            user_name: userName,
          });
          toast.success(
            "We’ll let you know when WhatsApp notifications are ready.",
          );
        }}
      />
    </form>
  );
}
